import React from "react";
import Reveal from "./Reveal";

/**
 * SplitPageHero — 40/60 text-image split hero used on inner pages (About, Fleet, Contact)
 * for visual consistency with the Home hero.
 */
export default function SplitPageHero({
  eyebrow,
  titleGreen,
  titleOrange,
  subtitle,
  imageWebp,
  imagePng,
  imageAlt,
  badgeLabel,
  badgeSub,
  children,
}) {
  return (
    <section className="relative bg-white" data-testid="page-hero">
      <div className="grid grid-cols-1 lg:grid-cols-[40fr_60fr] items-center gap-6 lg:gap-0">
        {/* LEFT — text */}
        <div className="relative px-6 md:px-12 lg:px-12 xl:px-16 py-12 md:py-16 lg:py-10 flex flex-col justify-center">
          <Reveal>
            <p className="section-tag" data-testid="page-hero-eyebrow">{eyebrow}</p>
          </Reveal>
          <Reveal delay={100}>
            <h1
              className="font-heading uppercase leading-[1.0] tracking-tight text-[30px] sm:text-[42px] md:text-[46px] xl:text-[54px] 2xl:text-[62px] mt-4"
              data-testid="page-hero-title"
            >
              {titleGreen && <span className="block text-brand-green">{titleGreen}</span>}
              {titleOrange && <span className="block text-brand-orange">{titleOrange}</span>}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-6 flex items-center gap-4">
              <span className="block w-14 h-1 bg-brand-orange" />
            </div>
          </Reveal>
          {subtitle && (
            <Reveal delay={280}>
              <p className="mt-5 font-body text-base md:text-[17px] text-zinc-700 leading-relaxed max-w-xl">
                {subtitle}
              </p>
            </Reveal>
          )}
          {children && (
            <Reveal delay={360}>
              <div className="mt-8 flex flex-wrap items-center gap-4">{children}</div>
            </Reveal>
          )}
        </div>

        {/* RIGHT — image */}
        <div className="relative w-full self-stretch flex items-center bg-white" data-testid="page-hero-image">
          <picture className="block w-full">
            <source srcSet={imageWebp} type="image/webp" />
            <img
              src={imagePng}
              alt={imageAlt}
              width="1400"
              height="800"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              className="w-full h-auto block object-contain"
            />
          </picture>
          <div
            className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-28 pointer-events-none hidden lg:block"
            style={{ background: "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)" }}
          />
          {(badgeLabel || badgeSub) && (
            <div className="absolute top-4 right-4 lg:top-6 lg:right-6 bg-white/95 backdrop-blur px-4 py-2 border-l-4 border-brand-orange shadow-lg">
              {badgeLabel && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{badgeLabel}</p>
              )}
              {badgeSub && <p className="font-heading text-sm text-brand-green">{badgeSub}</p>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
